/**
 * "Your PulseBoard profile, last N days" — first-party traffic proof shown
 * to specialists and practice managers (claim links today; the specialist
 * dashboard later). Only aggregate counts; numbers are never rounded up.
 */
export interface ProfileTrafficSummary {
  days: number;
  from: string;
  to: string;
  views: number;
  searchViews: number;
  searchLandings: number;
  publicViews: number;
  clinicianViews: number;
  phoneClicks: number;
  websiteClicks: number;
  emailClicks: number;
  referralActions: number;
}

export function ProfileTrafficCard({
  traffic,
  profileCount,
}: {
  traffic: ProfileTrafficSummary;
  profileCount: number;
}) {
  const subject = profileCount === 1 ? "this profile" : `these ${profileCount} profiles`;
  const contactActions = traffic.phoneClicks + traffic.websiteClicks + traffic.emailClicks;
  const stats: Array<{ value: number; label: string }> = [
    { value: traffic.views, label: "profile visits" },
    { value: traffic.searchLandings, label: "arrived straight from Google or Bing" },
    { value: contactActions, label: "calls, website and email clicks" },
    { value: traffic.referralActions, label: "referral details copied or letters drafted" },
  ];

  return (
    <section className="ds-panel ds-panel--tint" aria-labelledby="claim-traffic-title">
      <div className="ds-stack" style={{ gap: 6 }}>
        <p className="ds-eyebrow ds-eyebrow--teal">Last {traffic.days} days on PulseBoard</p>
        <h2 id="claim-traffic-title" className="ds-title">Who is already finding {subject}</h2>
      </div>
      {traffic.views === 0 ? (
        <p className="ds-body">
          No visits recorded in the last {traffic.days} days yet. Profiles with confirmed availability and referral
          details are the ones patients and GPs find and act on.
        </p>
      ) : (
        <>
          <dl className="ds-stats">
            {stats.map((stat) => (
              <div key={stat.label}>
                <dd className="ds-num">{stat.value.toLocaleString("en-AU")}</dd>
                <dt>{stat.label}</dt>
              </div>
            ))}
          </dl>
          <p className="ds-fine">
            {formatDay(traffic.from)} – {formatDay(traffic.to)}. Counted by PulseBoard once per visit, bots excluded;{" "}
            {traffic.publicViews.toLocaleString("en-AU")} by patients and the public,{" "}
            {traffic.clinicianViews.toLocaleString("en-AU")} by clinicians.
          </p>
        </>
      )}
    </section>
  );
}

function formatDay(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short" }).format(date);
}
